import React, { useEffect, useState } from 'react'
import { Delete, Download, Edit, ListAltRounded } from '@mui/icons-material'
import { useStores } from '@trivie/core'
import { ConfirmDialog } from '@trivie/ui-web/components/confirm-dialog'
import { DataTable, TableHelper } from '@trivie/ui-web/components/data-table/data-table'
import { observer } from 'mobx-react-lite'
import { CircularProgress } from '@mui/material'
import { Toolbar } from '@trivie/ui-web/components/table-toolbar'
import { AssignmentCreateDialog } from '../screens/activities/assignments/create-assignment/assignment-create'

interface CardTableProps {
  tableHelper: TableHelper
  openFilter?: boolean
  exportFilename?: string
}

export const CardTable = observer((props: CardTableProps) => {
  const { exportFilename, tableHelper, openFilter } = props

  const { manageUserStore, cardStore, topicsStore, contentCreationStore } = useStores()
  const { checkDeleteCard, deleteCard } = cardStore
  const { gdpr, permissions } = manageUserStore

  const [selectedRows, setSelectedRows] = useState<any>([])
  const [loading, setLoading] = useState<boolean>(tableHelper.custom ? false : true)

  // Dialogs
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [deleteCheck, setDeleteCheck] = useState<any>()
  const [showAssignWizard, setShowAssignWizard] = useState(false)

  const getActions = () => {
    switch (tableHelper.parent) {
      case 'assignments':
        return []
      case 'quizzes':
        return []
      case 'groups':
        return []
      case 'tags':
        return []
      case 'users':
        return []
      default:
        return [
          {
            label: 'Delete',
            icon: Delete,
            onClick: () => {
              checkDeleteCard(selectedRows[0]).then((data) => {
                setDeleteCheck(data?.message)
                setShowConfirmDelete(true)
              })
            },
            disabled: !permissions?.permissions.some((p) => p.code === 'can_delete_card'),
          },
        ]
    }
  }

  const [tableActions, setTableActions] = useState<any>(getActions())

  useEffect(() => {
    if (permissions) {
      setTableActions(getActions())
    }
  }, [permissions?.permissions])

  useEffect(() => {
    setTableActions(getActions())
  }, [selectedRows])

  useEffect(() => {
    load()
  }, [])

  const load = () => {
    if (!tableHelper.custom) {
      setLoading(true)
      tableHelper.load().then(() => setLoading(false))
    }
  }

  const save = async (columns: any, filter: any) => {
    setLoading(true)
    tableHelper.save(columns, filter).then(() => setLoading(false))
  }

  return loading ? (
    <div style={LOADER}>
      <CircularProgress />
    </div>
  ) : (
    <>
      <DataTable
        gdpr={gdpr}
        allowNewReport
        openFilter={openFilter}
        columnVisibilityModel={tableHelper.columnVisibilityModel}
        hideAddColumn
        tableHelper={tableHelper}
        filterModel={tableHelper.filterModel}
        sortModel={tableHelper.sortModel}
        searchPlaceholder="Search Info Cards"
        rows={tableHelper.rows}
        columns={tableHelper.columns}
        onSaveLayout={tableHelper?.saveLayout}
        onSaveFilters={save}
        toolbar={<Toolbar selected={selectedRows} actions={tableActions} />}
        exportFilename={
          exportFilename
            ? exportFilename
            : tableHelper.parent !== 'cards'
              ? `${tableHelper.parent}_cards`
              : 'cards'
        }
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={(param) => setSelectedRows(param)}
      />
      {showConfirmDelete && (
        <ConfirmDialog
          danger
          open={showConfirmDelete}
          title="Remove Cards"
          message={`${
            deleteCheck
              ? deleteCheck
              : `Are you sure you want to delete ${selectedRows.length} card${
                  selectedRows.length > 1 ? 's' : ''
                }?`
          }`}
          confirmedMessage={`${selectedRows.length} card${
            selectedRows.length > 1 ? 's have' : ' has'
          } been removed.`}
          contentStyle={deleteCheck ? { textAlign: 'left' } : {}}
          confirmButton="Confirm"
          onConfirm={() => deleteCard(selectedRows[0])}
          handleCancel={() => setShowConfirmDelete(false)}
          onClose={() => {
            setSelectedRows([])
            load()
            setShowConfirmDelete(false)
          }}
        />
      )}
    </>
  )
})

const LOADER: React.CSSProperties = {
  display: 'flex',
  flex: 1,
  justifyContent: 'center',
  flexDirection: 'column',
  alignItems: 'center',
  height: window.innerHeight / 1.5,
}
